// Build a fresh World from map + balance data and a seed.
import { createRng } from './rng';
import { newId, spawnTownHomes } from './spawn';
import type { Balance, MapData, Plant, Substation, Tap, World } from './types';

const START_HOUR = 7;

export function substationRadius(map: MapData, balance: Balance): number {
  return map.size.w * balance.substation.radiusFractionOfMapWidth;
}

export function makePlant(
  world: World,
  balance: Balance,
  type: Plant['type'],
  x: number,
  y: number,
): Plant {
  const spec = balance.plants[type];
  return {
    id: newId(world, 'p'),
    type,
    x,
    y,
    capacity: spec.capacityPerHalfDay,
    maxTaps: spec.maxTaps,
    builtOnDay: world.time.day,
    onlineDay: world.time.day + spec.leadDays,
  };
}

export function makeSubstation(
  world: World,
  map: MapData,
  balance: Balance,
  x: number,
  y: number,
): Substation {
  return {
    id: newId(world, 's'),
    x,
    y,
    radius: substationRadius(map, balance),
    capacity: balance.substation.capacityPerHalfDay,
    batteries: 0,
    charge: 0,
  };
}

export function makeTap(world: World, plantId: string, substationId: string): Tap {
  return { id: newId(world, 't'), plantId, substationId, highVoltage: false };
}

export function createWorld(map: MapData, balance: Balance, seed: number): World {
  const startingCash = map.economy.startingCash ?? balance.economy.startingCash;
  const firstSeason = map.seasons.order[0] ?? 'spring';
  const world: World = {
    seed,
    rng: createRng(seed),
    mapId: map.id,
    // Start at sunrise so the first thing a player sees is the day beginning.
    time: {
      tick: START_HOUR * balance.time.ticksPerHour,
      hour: START_HOUR,
      day: 1,
      season: firstSeason,
    },
    cash: startingCash,
    fuelPrice: balance.economy.fuelPriceDay1,
    homes: [],
    substations: [],
    plants: [],
    taps: [],
    weather: { cloud: 0, windy: false, active: [], forecast: [] },
    score: { homeDays: 0, peakHomes: 0, daysSurvived: 0 },
    status: 'running',
    speed: 1,
    nextId: 1,
    growthCarry: 0,
  };

  for (const town of map.towns) {
    if (town.startHomes > 0) spawnTownHomes(world, map, balance, town, town.startHomes);
  }

  for (const p of map.start.plants) world.plants.push(makePlant(world, balance, p.type, p.x, p.y));
  for (const s of map.start.substations) {
    world.substations.push(makeSubstation(world, map, balance, s.x, s.y));
  }
  for (const [pi, si] of map.start.taps) {
    const plant = world.plants[pi];
    const sub = world.substations[si];
    if (plant && sub) world.taps.push(makeTap(world, plant.id, sub.id));
  }
  return world;
}
